const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://api.volailledor.logicieels.cloud/api';
import type { User } from '../types/auth';

class ApiService {
  /** @deprecated Token is now managed via HttpOnly cookie */
  setToken(_token: string) {
    // No-op: token is set via HttpOnly cookie by the server
  }

  clearToken() {
    localStorage.removeItem('user_data');
  }

  private getHeaders(isFormData = false): HeadersInit {
    const headers: HeadersInit = {
      'Accept': 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    };

    if (!isFormData) {
      headers['Content-Type'] = 'application/json';
    }

    return headers;
  }

  async request<T>(endpoint: string, options: RequestInit & { silent?: boolean; timeoutMs?: number } = {}): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`;
    const isFormData = options.body instanceof FormData;
    const silent = options.silent || false;
    // Sans ceci, une connexion coupee en plein encaissement laissait le bouton
    // bloque sur "Traitement..." indefiniment, sans jamais avertir la vendeuse
    // qu'il fallait reessayer (vecu reel a AZIMO, cote caisse).
    const timeoutMs = options.timeoutMs ?? (isFormData ? 60000 : 25000);
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

    try {
      const response = await fetch(url, {
        ...options,
        credentials: 'include',
        signal: controller.signal,
        headers: {
          ...this.getHeaders(isFormData),
          ...options.headers,
        },
      });
      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        if (!silent) {
          console.error('API Error:', response.status, errorData);
          console.error('Détails de l\'erreur:', errorData);
        }
        
        let errorMessage = `Erreur ${response.status}`;
        
        if (response.status === 401) {
          // Check if it's a real auth failure (no token or invalid token)
          // vs a resource-specific 401 (e.g., user not assigned to a shop)
          const isAuthEndpoint = endpoint.includes('/auth/');
          const message = errorData.message || 'Session expirée';

          // Only auto-redirect for actual authentication failures
          // Let other 401s be handled by the components
          if (message.toLowerCase().includes('invalid') ||
              message.toLowerCase().includes('expired') ||
              message.toLowerCase().includes('authentication required')) {
            errorMessage = 'Session expirée. Veuillez vous reconnecter.';
            this.clearToken();
            // Only redirect for auth endpoints or clear token failures
            if (isAuthEndpoint && window.location.pathname !== '/login') {
              window.location.href = '/login';
            }
          } else {
            errorMessage = message;
          }
        } else if (response.status === 403) {
          errorMessage = errorData.message || 'Accès refusé. Permissions insuffisantes.';
          // Inclure les informations de debug si disponibles
          if (errorData.debug) {
            console.error('Debug permissions:', errorData.debug);
            const userRoles = errorData.debug.userRoles?.join(', ') || 'Aucun';
            const requiredRoles = errorData.debug.requiredRoles?.join(', ') || 'Non spécifié';
            errorMessage += `\n\nVos rôles: ${userRoles}\nRôles requis: ${requiredRoles}`;
          }
        } else if (response.status === 404) {
          errorMessage = errorData.message || 'Ressource non trouvée.';
        } else if (response.status === 409) {
          // Conflict - resource already exists (duplicate email, phone, etc.)
          // Translate common English messages to French
          const message = errorData.message || '';
          if (message.toLowerCase().includes('resource already exists') || message === '') {
            // Check the endpoint to provide a more specific message
            if (endpoint.includes('restock-request') || endpoint.includes('restock')) {
              errorMessage = 'Une demande de ravitaillement similaire existe déjà. Veuillez vérifier les demandes en cours.';
            } else if (endpoint.includes('user') || endpoint.includes('auth')) {
              errorMessage = 'Un utilisateur avec ces informations existe déjà.';
            } else {
              errorMessage = 'Cette ressource existe déjà.';
            }
          } else {
            errorMessage = message;
          }
        } else if (response.status === 400) {
          errorMessage = errorData.error || errorData.message || 'Requête invalide.';
          if (!silent) {
            console.error('Erreur 400 détaillée:', errorData);
            console.error('URL de la requête:', url);
            console.error('Corps de la requête:', options.body);
            console.error('Headers:', this.getHeaders(isFormData));
          }

          // Si c'est une erreur de validation, afficher les détails.
          // Le serveur renvoie un tableau d'objets {field, message}. Le code
          // precedent faisait Object.values(...).join(', ') en supposant des
          // chaines : concatener des objets donnait « [object Object] », et la
          // vraie cause du refus restait invisible.
          if (errorData.errors) {
            if (!silent) console.error('Erreurs de validation:', errorData.errors);
            const brut = Array.isArray(errorData.errors)
              ? errorData.errors
              : Object.values(errorData.errors).flat();
            const details = brut
              .map((e: any) => {
                if (typeof e === 'string') return e;
                if (e && typeof e === 'object') return e.field ? `${e.field} : ${e.message}` : e.message;
                return null;
              })
              .filter(Boolean);
            if (details.length > 0) errorMessage = details.join(' • ');
          }
        } else if (response.status === 500) {
          errorMessage = 'Erreur serveur. Le service pourrait ne pas être disponible.';
          if (!silent) {
            console.error('Server Error Details:', errorData);
            console.error('Request URL:', url);
            console.error('Request Options:', options);
          }
        } else if (errorData.message) {
          errorMessage = errorData.message;
        }
        
        // Le statut est attache a l'erreur : sans lui, l'appelant ne peut pas
        // distinguer une session invalide (401) d'un quota atteint ou d'une
        // panne serveur, et traitait les trois de la meme facon.
        const erreur = new Error(errorMessage) as Error & { status?: number };
        erreur.status = response.status;
        throw erreur;
      }

      const data = await response.json();
      return data;
    } catch (error) {
      clearTimeout(timeoutId);
      if (error instanceof Error && error.name === 'AbortError') {
        const erreur = new Error('Connexion trop lente ou coupée. Vérifiez votre connexion internet et réessayez.');
        if (!silent) console.error('Timeout:', endpoint);
        throw erreur;
      }
      if (!silent) console.error('Network Error:', error);
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Erreur de connexion réseau');
    }
  }

  // Auth endpoints
  async login(email: string, password: string, twoFactorCode?: string): Promise<{ success: boolean; data: { user?: User; requiresTwoFactor?: boolean; userId?: string } }> {
    return this.request('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ identifier: email, password, twoFactorCode }),
    });
  }

  async me(): Promise<{ success: boolean; data: User }> {
    return this.request<{ success: boolean; data: User }>('/auth/me');
  }

  async logout() {
    return this.request('/auth/logout', { method: 'POST' });
  }

  // 2FA endpoints
  async generate2FA(): Promise<{ success: boolean; data: { secret: string; qrCode: string } }> {
    return this.request('/auth/2fa/generate', { method: 'POST' });
  }

  async enable2FA(code: string): Promise<{ success: boolean; data: { message: string } }> {
    return this.request('/auth/2fa/enable', { method: 'POST', body: JSON.stringify({ code }) });
  }

  async disable2FA(code: string): Promise<{ success: boolean; data: { message: string } }> {
    return this.request('/auth/2fa/disable', { method: 'POST', body: JSON.stringify({ code }) });
  }

  async get2FAStatus(): Promise<{ success: boolean; data: { twoFactorEnabled: boolean } }> {
    return this.request('/auth/2fa/status');
  }
}

export const apiService = new ApiService();